/** @jsx jsx */
/**
 * Help-center side of the Hub Forge/Connect renderer integration.
 *
 * The platform-level packages (`@atlassian/hub-forge-extension`,
 * `@atlassian/hub-connect-extension`) ship the ADF schema, manifests and
 * empty-state UI for the four new editor elements (Forge header / footer,
 * Connect header / footer). They intentionally do NOT depend on any Forge
 * or Connect SDK — the host product is expected to inject a concrete
 * renderer via the `HubForge|ConnectRendererProvider` context.
 *
 * This module is that injection point for the Help Hub / Customer Service
 * Hub. It mirrors how the legacy help-center Forge/Connect customisation
 * elements render today (see
 * `help-center/packages/help-center/customisation/elements/forge`
 * and `…/elements/connect`):
 *  - same `<ElementRoot>` wrapper (margin/padding tokens, flexibility flag)
 *  - same pointer-events shield while editing (gated by `mode === EDITOR`)
 *  - same `<ConnectFragment>` fan-out for `headerAndSubheaderPanels`
 *  - same `trackError` analytics on invalid `elementType`s
 *
 * "Is Connect installed on the tenant?" / "Is Forge installed?" are
 * decided server-side: the BE only emits a `HubForgeExtension` /
 * `HubConnectExtension` ADF node when the corresponding app is available
 * for the slot. At render time the legacy `<ForgeHeaderModule>` /
 * `<ConnectFragment>` primitives perform a second, finer-grained check
 * (their internal Relay queries return empty when there are no matching
 * Forge/Connect modules, in which case they render nothing — matching the
 * legacy element behaviour).
 *
 * Mounted through `HubAppProps.productExtensionsDependenciesProvider` so
 * platform extension renderers can resolve Help Center's concrete Forge / Connect
 * primitives from both published renderer and editor extension roots.
 */

import React, { type ReactNode } from 'react';
import { jsx } from '@compiled/react';
import { css } from '@atlaskit/css';
import FeatureGates from '@atlaskit/feature-gate-js-client/feature-gates';

import { IntlProvider } from 'react-intl';

import { connect } from 'react-redux';

import {
	HubConnectRendererProvider,
	type HubConnectRenderer,
} from '@atlassian/hub-connect-extension/controllers/use-hub-connect-renderer';
import { ConnectElementType } from '@atlassian/hub-connect-extension/constants';
import { HubConnectEmptyState } from '@atlassian/hub-connect-extension/hub-connect-empty-state';
import {
	HubForgeRendererProvider,
	type HubForgeRenderer,
} from '@atlassian/hub-forge-extension/controllers/use-hub-forge-renderer';
import { ForgeElementType } from '@atlassian/hub-forge-extension/constants';
import { HubForgeEmptyState } from '@atlassian/hub-forge-extension/hub-forge-empty-state';
import { CommonHubExtensionsDependenciesProvider } from '@atlassian/common-hub-extensions-dependencies-provider';

import { HELP_CENTER, ConnectFragment } from '@helpcenter/connect-fragment';
import { isIncreasedFlexibilityEnabled } from '@helpcenter/feature-flags';
import { getLanguageFromLocale } from '@atlassian/help-center-common-util/locale-data';

import { store } from 'providers';
import { ElementRoot, ElementWidth } from 'view/common/element-root';
import { ForgeFooterModule } from 'view/forge-ui/portal-footer';
import { ForgeHeaderModule } from 'view/forge-ui/portal-header';
import { useTFExtensionList } from 'view/common/forge-ui/use-tf-extension-list';
import { ExtensionMode } from '@atlassian/company-hub-common/types';
import type { ProductDependenciesProviderProps } from '@atlassian/company-hub-common/controllers/use-product-dependencies';
import { HubPremiumSlaElementsProvider } from 'view/hub-premium-sla';
import {
	PORTAL_HEADER_MODULE,
	PORTAL_SUBHEADER_MODULE,
	PORTAL_FOOTER_MODULE,
	FORGE_HELP_CENTER,
	FORGE_CSM_HELP_CENTER,
	RESOURCE_TYPE_PROJECT,
} from '@helpcenter/forge-ui-utils/src/constants';
import type { ForgePageName, ResourceContext } from '@helpcenter/forge-ui-utils/src/types';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { isCSMHelpCenter } from '@atlassian/help-center-common-util/advanced-help-center';
import { getEnv } from '@atlassian/help-center-common-util/env';
import { getConnectAddonPanels, isConnectFragmentsResolved } from 'state/selectors/connect-addon';
import type { ConnectFragmentLocation } from 'state/selectors/connect-addon';
import { fetchPortalAction } from 'state/actions/portal';
import { getPortalOrError, getProjectId } from 'state/selectors/portal';
import { getLocale } from 'state/selectors/user';
import type { State } from 'state';

import { trackError } from '@atlassian/help-center-common-util/analytics';

// ---------------------------------------------------------------------------
// Forge bridge
// ---------------------------------------------------------------------------

// Surface-aware Forge page name; the CSM surface is guarded by forge-on-csm-hubs (else JSM Hub page).
const getForgePage = (): ForgePageName =>
	fg('forge-on-csm-hubs') && isCSMHelpCenter(getEnv().helpCenterType)
		? FORGE_CSM_HELP_CENTER
		: FORGE_HELP_CENTER;

const USE_USER_LOCALE_FOR_FORGE = 'use_user_locale_for_forge';

const isUseUserLocaleForForgeEnabled = (): boolean =>
	FeatureGates.checkGate(USE_USER_LOCALE_FOR_FORGE);

const forgeModuleProps = {
	noWrapper: true,
	allowCustomPadding: true,
	skipHelpCenterWrapper: true,
} as const;

interface ForgeSlotOwnProps {
	isPageInEditMode: boolean;
}

interface ForgeSlotResourceProps {
	/**
	 * The portal id for the current hub page, parsed from the URL (see
	 * {@link getPortalIdFromUrl}). Present only on hub portal pages.
	 */
	portalId?: number;
	/**
	 * The project id of the current portal, resolved from the portal model in
	 * Redux. Passed down to the Forge modules so the fetch-extensions requests
	 * they issue are scoped to that project
	 * (`{ resourceId, resourceType: 'PROJECT' }`), matching the classic (non-hub)
	 * portal behaviour. `undefined` off a portal page or before the portal model
	 * has loaded.
	 *
	 * A primitive, so `connect` returns a stable value and effects keyed on it do
	 * not re-run on unrelated re-renders.
	 */
	projectId?: number;
	/**
	 * Whether the portal model is already present in Redux. When it is not, the
	 * slot triggers a fetch so the project (and therefore the resource context)
	 * can resolve.
	 */
	isPortalModelLoaded: boolean;
}

interface ForgeSlotDispatchProps {
	fetchPortal: typeof fetchPortalAction;
}

/**
 * Extracts the portal id from the current URL.
 *
 * The hub Forge slots render inside the editor's detached React root (see the
 * `HubExtensionsDependenciesProvider` docblock), where the route-level
 * `react-resource-router` match is not available. The portal id is, however,
 * always present in the pathname on a portal page — both the customer-facing
 * view route (`/portal/<id>/…`) and the hub editor route
 * (`/<helpcenterId>/edit-hub/portal/<id>`) contain a `/portal/<digits>`
 * segment — so we read it from there to work in both roots.
 *
 * Returns `undefined` on non-portal pages.
 */
export const getPortalIdFromUrl = (pathname: string): number | undefined => {
	const match = /\/portal\/(\d+)(?:\/|$)/.exec(pathname);
	if (!match) {
		return undefined;
	}
	const portalId = Number(match[1]);
	return Number.isSafeInteger(portalId) && portalId > 0 ? portalId : undefined;
};

/**
 * Resolves the portal's project id from the portal model in Redux, keyed on the
 * portal id parsed from the URL. `react-redux` here is v6 (no hooks), so this is
 * resolved through `connect`. Returns primitives so the mapped values are
 * referentially stable across re-renders.
 */
const mapResourceFromState = (state: State): ForgeSlotResourceProps => {
	// CSM help centers have no "portal" concept, so the getPortalIdFromUrl path below does not apply;
	// use the hub's hoisted project id as the Forge resource context instead (gated by forge-on-csm-hubs).
	if (fg('forge-on-csm-hubs') && isCSMHelpCenter(getEnv().helpCenterType)) {
		return {
			portalId: undefined,
			projectId: getEnv().hoistedProjectId,
			isPortalModelLoaded: false,
		};
	}

	const portalId = getPortalIdFromUrl(globalThis.location?.pathname ?? '');
	if (!portalId) {
		return { portalId: undefined, projectId: undefined, isPortalModelLoaded: false };
	}

	return {
		portalId,
		projectId: getProjectId(state, portalId),
		// The portal record (success or error) is present once a fetch settled.
		isPortalModelLoaded: Boolean(getPortalOrError(state, portalId)),
	};
};

const mapDispatch: ForgeSlotDispatchProps = { fetchPortal: fetchPortalAction };

/**
 * Ensures the current portal model is fetched into Redux so `getProjectId` can
 * resolve the portal's project in both edit and view mode. Only fetches when a
 * portal page is detected and the model is not already loaded. Rendering never
 * waits on this fetch — the resource context is simply applied once the project
 * resolves.
 */
const useEnsurePortalFetched = (
	portalId: number | undefined,
	isPortalModelLoaded: boolean,
	fetchPortal: typeof fetchPortalAction,
) => {
	React.useEffect(() => {
		if (portalId && !isPortalModelLoaded) {
			fetchPortal({ id: portalId });
		}
	}, [portalId, isPortalModelLoaded, fetchPortal]);
};

/**
 * Builds a referentially-stable resource context from the primitive project id,
 * so the fetch effect that depends on it does not re-run on every render.
 */
const useProjectResourceContext = (projectId?: number): ResourceContext | undefined =>
	React.useMemo(
		() =>
			projectId
				? { resourceId: projectId.toString(), resourceType: RESOURCE_TYPE_PROJECT }
				: undefined,
		[projectId],
	);

/**
 * Whether the portal resource context has settled, i.e. it will not change from
 * "absent" to "present" on a later render:
 * - not a portal page (no `portalId`) -> nothing to resolve, and
 * - a portal page whose portal model is already in Redux.
 *
 * The empty-state probes wait for this so they issue a single, correctly-scoped
 * request instead of one without the resource context followed by one with it.
 */
const isResourceContextSettled = (portalId: number | undefined, isPortalModelLoaded: boolean) =>
	!portalId || isPortalModelLoaded;

/**
 * Editor-only probe for the header + subheader modules: reports whether any
 * apps are installed so the editor can show a placeholder for an empty slot.
 *
 * This is deliberately not rendered in the published view, where the result is
 * unused and the requests would be wasted — the modules fetch what they need to
 * render themselves.
 */
const ForgeHeaderEmptyStateProbe: React.FC<{ projectId?: number }> = ({ projectId }) => {
	const forgePage = getForgePage();
	const resourceContext = useProjectResourceContext(projectId);
	const { extensions: headerExtensions, resolved: headerResolved } = useTFExtensionList({
		resourceContext,
		type: PORTAL_HEADER_MODULE,
		page: forgePage,
	});
	const { extensions: subheaderExtensions, resolved: subheaderResolved } = useTFExtensionList({
		resourceContext,
		type: PORTAL_SUBHEADER_MODULE,
		page: forgePage,
	});

	// Wait for this probe to settle before mounting the modules. The modules
	// request the same extension types, and the cache only holds settled results,
	// so mounting them straight away would race this request and duplicate it.
	// Once this resolves their lookup hits the warm cache instead. Bounded:
	// `resolved` settles on both success and failure.
	if (!headerResolved || !subheaderResolved) {
		return null;
	}

	const isEmpty = (headerExtensions?.length ?? 0) === 0 && (subheaderExtensions?.length ?? 0) === 0;
	if (isEmpty) {
		return <HubForgeEmptyState />;
	}

	return <ForgeHeaderModule {...forgeModuleProps} page={forgePage} projectId={projectId} />;
};

/**
 * Header slot: renders the Forge header + subheader modules. In the editor,
 * once the trusted-fetch resolves with no installed apps for either module,
 * it shows the empty-state placeholder instead so authors know the slot is
 * empty. In the published view the modules self-render nothing when empty.
 *
 * On a portal page nothing is mounted until the resource context has settled:
 * mounting the modules with an unresolved project would make them fetch without
 * the resource context and then fetch again once it resolved. The wait is
 * bounded — `isPortalModelLoaded` settles whether the portal request succeeds or
 * fails — and does not apply off portal pages.
 */
const ForgeHeaderSlotInner: React.FC<
	ForgeSlotOwnProps & ForgeSlotResourceProps & ForgeSlotDispatchProps
> = ({ isPageInEditMode, portalId, projectId, isPortalModelLoaded, fetchPortal }) => {
	const forgePage = getForgePage();
	useEnsurePortalFetched(portalId, isPortalModelLoaded, fetchPortal);

	if (!isResourceContextSettled(portalId, isPortalModelLoaded)) {
		return null;
	}

	if (isPageInEditMode) {
		return <ForgeHeaderEmptyStateProbe projectId={projectId} />;
	}

	return <ForgeHeaderModule {...forgeModuleProps} page={forgePage} projectId={projectId} />;
};

const ForgeHeaderSlot = connect(mapResourceFromState, mapDispatch)(ForgeHeaderSlotInner);

/**
 * Editor-only probe for the footer module. See
 * {@link ForgeHeaderEmptyStateProbe}.
 */
const ForgeFooterEmptyStateProbe: React.FC<{ projectId?: number }> = ({ projectId }) => {
	const forgePage = getForgePage();
	const resourceContext = useProjectResourceContext(projectId);
	const { extensions, resolved } = useTFExtensionList({
		resourceContext,
		type: PORTAL_FOOTER_MODULE,
		page: forgePage,
	});

	// See ForgeHeaderEmptyStateProbe: wait for this request to settle so the
	// module's lookup for the same type hits the warm cache instead of racing it.
	if (!resolved) {
		return null;
	}

	if ((extensions?.length ?? 0) === 0) {
		return <HubForgeEmptyState />;
	}

	return <ForgeFooterModule {...forgeModuleProps} page={forgePage} projectId={projectId} />;
};

/**
 * Footer slot: same behaviour as {@link ForgeHeaderSlot} for the single
 * footer module type.
 */
const ForgeFooterSlotInner: React.FC<
	ForgeSlotOwnProps & ForgeSlotResourceProps & ForgeSlotDispatchProps
> = ({ isPageInEditMode, portalId, projectId, isPortalModelLoaded, fetchPortal }) => {
	const forgePage = getForgePage();
	useEnsurePortalFetched(portalId, isPortalModelLoaded, fetchPortal);

	if (!isResourceContextSettled(portalId, isPortalModelLoaded)) {
		return null;
	}

	if (isPageInEditMode) {
		return <ForgeFooterEmptyStateProbe projectId={projectId} />;
	}

	return <ForgeFooterModule {...forgeModuleProps} page={forgePage} projectId={projectId} />;
};

const ForgeFooterSlot = connect(mapResourceFromState, mapDispatch)(ForgeFooterSlotInner);

/**
 * Adapter from the platform-level Forge renderer signature to the legacy
 * help-center Forge header / footer modules.
 *
 * `'help_center'` is the canonical Forge page name (see
 * `JsmForgePageName`) used by the legacy `<ForgeElement />` for the same
 * header/footer slots. `noWrapper` + `allowCustomPadding` match the legacy
 * invocation so visual treatment is identical across legacy help-center
 * and the new hub editor.
 *
 * Returning `null` for unknown `elementType`s (rather than throwing) keeps
 * the editor render loop deterministic — the empty state owned by
 * `<HubForgeExtension />` is the user-visible fallback.
 */
const ForgeBridge: React.FC<Parameters<HubForgeRenderer>[0]> = ({ extensionNode, mode }) => {
	const elementType = extensionNode.parameters?.elementType;
	const isPageInEditMode = mode === ExtensionMode.EDITOR;
	const isFullWidthLayout = extensionNode.layout === 'full-width';
	const elementWidth = getElementWidth(extensionNode.layout);

	const isValid =
		elementType === ForgeElementType.HEADER_AND_SUB_HEADER_PANELS ||
		elementType === ForgeElementType.FOOTER_PANELS;

	React.useEffect(() => {
		if (!isValid) {
			trackError(
				'hub.forge.element.type.invalid',
				{ elementType },
				'Invalid Hub Forge Element Type received',
			);
		}
	}, [isValid, elementType]);

	if (!isValid) {
		return null;
	}

	const inner =
		elementType === ForgeElementType.HEADER_AND_SUB_HEADER_PANELS ? (
			<ForgeHeaderSlot isPageInEditMode={isPageInEditMode} />
		) : (
			<ForgeFooterSlot isPageInEditMode={isPageInEditMode} />
		);

	return (
		<div css={isFullWidthLayout && fullWidthRootCss}>
			<ElementRoot
				removePadding
				removeMargin={isIncreasedFlexibilityEnabled()}
				elementWidth={elementWidth}
			>
				<div
					data-hub-extension-bridge-content="forge"
					css={[fullWidthCss, isPageInEditMode && editModeCss]}
				>
					{inner}
				</div>
			</ElementRoot>
		</div>
	);
};

const renderHubForge: HubForgeRenderer = (props) => <ForgeBridge {...props} />;

// ---------------------------------------------------------------------------
// Connect bridge
// ---------------------------------------------------------------------------

/**
 * Connect panels are Redux-backed: `<ConnectFragment>` fetches them on mount
 * and the result lands in the store keyed by page + location. The selector
 * returns `undefined` until the fetch settles, then an array (possibly empty)
 * or an error. We treat "all requested locations resolved to an empty array"
 * as "no Connect app for this slot" and show the editor empty state. The
 * `<ConnectFragment>`s are still rendered so they keep ownership of fetching
 * and of rendering the real app when present.
 */
const connectLocationsHaveApp = (
	state: State,
	locations: ConnectFragmentLocation[],
): boolean | undefined => {
	// Gate on the fetch lifecycle, not on the panel values. A successful fetch
	// that returns no apps stores `undefined`/`[]` for individual locations,
	// which is indistinguishable from "not fetched yet" if we only look at the
	// panel values — that bug kept the empty state hidden forever. Using the
	// page-level resolution signal lets the empty state appear once the fetch
	// settles, even when there are no Connect apps.
	if (!isConnectFragmentsResolved({ state, pageName: HELP_CENTER })) {
		return undefined;
	}
	// Resolved: report whether any requested location actually has a panel.
	return locations.some((location) => {
		const panel = getConnectAddonPanels({ state, location, pageName: HELP_CENTER });
		return Array.isArray(panel) && panel.length > 0;
	});
};

/**
 * Adapter from the platform-level Connect renderer signature to the
 * legacy help-center `ConnectFragment` for the help-center page.
 *
 * For the header slot we render both `headerPanels` and `subheaderPanels`
 * to match the legacy `<ConnectElement />` behaviour — Connect apps that
 * registered against either location continue to render in the new hub
 * editor with zero migration work.
 */
const ConnectBridge: React.FC<Parameters<HubConnectRenderer>[0]> = ({ extensionNode, mode }) => {
	const elementType = extensionNode.parameters?.elementType;
	const isPageInEditMode = mode === ExtensionMode.EDITOR;
	const isFullWidthLayout = extensionNode.layout === 'full-width';
	const elementWidth = getElementWidth(extensionNode.layout);

	const isValid =
		elementType === ConnectElementType.HEADER_AND_SUB_HEADER_PANELS ||
		elementType === ConnectElementType.FOOTER_PANELS;

	React.useEffect(() => {
		if (!isValid) {
			trackError(
				'hub.connect.element.type.invalid',
				{ elementType },
				'Invalid Hub Connect Element Type received',
			);
		}
	}, [isValid, elementType]);

	if (!isValid) {
		return null;
	}

	const locations =
		elementType === ConnectElementType.HEADER_AND_SUB_HEADER_PANELS
			? (['headerPanels', 'subheaderPanels'] as const)
			: (['footerPanels'] as const);

	return (
		<ConnectSlot
			isPageInEditMode={isPageInEditMode}
			locations={[...locations]}
			elementWidth={elementWidth}
			isFullWidthLayout={isFullWidthLayout}
		/>
	);
};

/**
 * Renders the Connect fragments for the given locations in the published
 * renderer. In the editor's detached React root we render the placeholder
 * directly, avoiding redux-backed ConnectFragment components there.
 */
interface ConnectSlotOwnProps {
	isPageInEditMode: boolean;
	locations: ConnectFragmentLocation[];
	elementWidth?: ElementWidth;
	isFullWidthLayout: boolean;
}

interface ConnectSlotStateProps {
	hasApp: boolean | undefined;
}

const ConnectPublishedSlotInner: React.FC<ConnectSlotOwnProps & ConnectSlotStateProps> = ({
	locations,
	hasApp,
	elementWidth,
	isFullWidthLayout,
}) => {
	if (hasApp === false) {
		return null;
	}

	return (
		<div css={isFullWidthLayout && fullWidthRootCss}>
			<ElementRoot
				removePadding
				removeMargin={isIncreasedFlexibilityEnabled()}
				elementWidth={elementWidth}
			>
				<div data-hub-extension-bridge-content="connect" css={fullWidthCss}>
					{locations.map((location) => (
						<ConnectFragment key={location} page={HELP_CENTER} location={location} />
					))}
				</div>
			</ElementRoot>
		</div>
	);
};

const ConnectPublishedSlot = connect(
	(state: State, ownProps: ConnectSlotOwnProps): ConnectSlotStateProps => ({
		hasApp: connectLocationsHaveApp(state, ownProps.locations),
	}),
)(ConnectPublishedSlotInner);

const ConnectEditorSlot: React.FC<
	Pick<ConnectSlotOwnProps, 'elementWidth' | 'isFullWidthLayout'>
> = ({ elementWidth, isFullWidthLayout }) => (
	<div css={isFullWidthLayout && fullWidthRootCss}>
		<ElementRoot
			removePadding
			removeMargin={isIncreasedFlexibilityEnabled()}
			elementWidth={elementWidth}
		>
			<div data-hub-extension-bridge-content="connect" css={[fullWidthCss, editModeCss]}>
				<HubConnectEmptyState />
			</div>
		</ElementRoot>
	</div>
);

const ConnectSlot: React.FC<ConnectSlotOwnProps> = ({
	isPageInEditMode,
	locations,
	elementWidth,
	isFullWidthLayout,
}) =>
	isPageInEditMode ? (
		<ConnectEditorSlot elementWidth={elementWidth} isFullWidthLayout={isFullWidthLayout} />
	) : (
		<ConnectPublishedSlot
			isPageInEditMode={false}
			locations={locations}
			elementWidth={elementWidth}
			isFullWidthLayout={isFullWidthLayout}
		/>
	);

const renderHubConnect: HubConnectRenderer = (props) => <ConnectBridge {...props} />;

// ---------------------------------------------------------------------------
// Internal provider wrapper
// ---------------------------------------------------------------------------

interface HubExtensionsBridgeProps {
	children: ReactNode;
}

/**
 * Single mount point that injects both Hub Forge and Hub Connect renderers.
 * Order of providers is irrelevant — they are independent contexts.
 */
const HubExtensionsBridge = ({ children }: HubExtensionsBridgeProps): React.JSX.Element => (
	<HubForgeRendererProvider renderer={renderHubForge}>
		<HubConnectRendererProvider renderer={renderHubConnect}>{children}</HubConnectRendererProvider>
	</HubForgeRendererProvider>
);

/**
 * Provider passed to the platform hub packages as
 * `productExtensionsDependenciesProvider` (see `HubAppProps`).
 *
 * The hub editor renders extension nodes as ReactNodeViews in its own
 * detached React root, so the route-level `<HubExtensionsBridge>` context
 * does not reach them. The platform wraps every extension node — in both
 * the published renderer and the editor — with this single provider, so it
 * must establish BOTH the base hub extension dependencies (via
 * `CommonHubExtensionsDependenciesProvider`, the default the platform uses
 * when no override is supplied) AND the Forge / Connect renderer bridge.
 */
export const HubExtensionsDependenciesProvider = ({
	children,
	extensionNode,
	mode,
}: {
	children?: ReactNode;
	extensionNode?: ProductDependenciesProviderProps['extensionNode'];
	mode?: ProductDependenciesProviderProps['mode'];
}): React.JSX.Element => (
	<IntlProvider
		locale={
			isUseUserLocaleForForgeEnabled()
				? getLanguageFromLocale(getLocale(store.getState() as State))
				: 'en'
		}
	>
		<CommonHubExtensionsDependenciesProvider>
			{/* Nested inside the common provider so it can extend the context that one establishes. */}
			<HubPremiumSlaElementsProvider extensionKey={extensionNode?.extensionKey} mode={mode}>
				<HubExtensionsBridge>{children}</HubExtensionsBridge>
			</HubPremiumSlaElementsProvider>
		</CommonHubExtensionsDependenciesProvider>
	</IntlProvider>
);

// ---------------------------------------------------------------------------
// Styles (declared at the bottom of the file per styled-component-order)
// ---------------------------------------------------------------------------

const getElementWidth = (layout: Parameters<HubForgeRenderer>[0]['extensionNode']['layout']) =>
	layout === 'full-width' ? ElementWidth.FULL_WIDTH : undefined;

const fullWidthRootCss = css({
	alignSelf: 'stretch',
	width: 'auto',
});

const fullWidthCss = css({ width: '100%' });
const editModeCss = css({ pointerEvents: 'none' });
